import { Controller } from "@hotwired/stimulus";

/**
 * Pricing Updater Controller
 *
 * Listens for date changes from the datepicker and updates the pricing display
 * via HTMX to show date-specific pricing.
 *
 * Usage:
 *   <div data-controller="pricing-updater"
 *        data-pricing-updater-url-value="/experiences/{uuid}/pricing/">
 *     ...pricing content...
 *   </div>
 */
export default class extends Controller {
  static values = {
    url: String,
  };

  declare urlValue: string;

  // Bind once and keep the reference so removeEventListener actually detaches it.
  // The element is replaced via outerHTML on each update (Stimulus then calls
  // disconnect), so a fresh bind here would leak a listener per swap and fire
  // the fetch — and the announcement — multiple times.
  private boundHandler = this.handleDateChange.bind(this) as EventListener;

  connect() {
    // Listen for date change events from the datepicker
    // The datepicker dispatches 'datechange' (lowercase) with bubbles: true
    window.addEventListener("datechange", this.boundHandler);
  }

  disconnect() {
    window.removeEventListener("datechange", this.boundHandler);
  }

  async handleDateChange(event: CustomEvent) {
    const { checkIn, checkOut } = event.detail;

    if (!checkIn || !checkOut) {
      console.debug("Pricing updater: Missing dates", { checkIn, checkOut });
      return;
    }

    console.debug("Pricing updater: Fetching pricing for dates", {
      checkIn,
      checkOut,
    });

    try {
      // Fetch new pricing HTML from the server
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set("arrival_date", checkIn);
      url.searchParams.set("departure_date", checkOut);

      const response = await fetch(url.toString());

      if (!response.ok) {
        console.error("Failed to fetch pricing:", response.statusText);
        return;
      }

      const html = await response.text();

      // Announce the new price into the persistent live region BEFORE swapping —
      // the swap replaces this element (and anything inside it), so the announcer
      // must live outside it (see _booking_card.html) and be read from here while
      // this element is still attached.
      this.announcePrice(html);

      // Replace this element with the new pricing HTML
      this.element.outerHTML = html;
    } catch (error) {
      console.error("Error fetching pricing:", error);
    }
  }

  private announcePrice(html: string): void {
    const region = this.element.parentElement?.querySelector<HTMLElement>(
      "[data-booking-price-status]",
    );
    if (!region) return;

    const parsed = new DOMParser().parseFromString(html, "text/html");
    const priceEl = parsed.getElementById("booking-card-pricing");
    const summary = priceEl?.textContent?.replace(/\s+/g, " ").trim() ?? "";
    region.textContent = summary ? `Updated price: ${summary}` : "";
  }
}
